/** @typedef {string | symbol | number} Property */

/**
 * Creates an object containing just the own properties of the chosen object.
 * This includes Symbol properties.
 * @param {object} object
 * @return {object}
 */
export function onlyOwnProperties(object) {
  return pickProperties(object, ...Object.getOwnPropertyNames(object).concat(Object.getOwnPropertySymbols(object)));
}

/**
 * Creates an object with only the chosen own properties from a chosen object.
 * Any properties in `properties` not present on the object will be omitted.
 * @param {object} object
 * @param {...Property} properties The properties to pick.
 * @return {object}
 */
export function pickProperties(object, ...properties) {
  const result = {};
  properties
    .filter((property) => property in object)
    .forEach((property) => result[property] = object[property]);
  return result;
}

/**
 * Creates a shallow copy of a chosen object with the chosen own properties omitted.
 * @param {object} object
 * @param  {...Property} properties The properties to omit.
 * @return {object}
 */
export function omitProperties(object, ...properties) {
  const result = {...object};
  properties.forEach((property) => delete result[property]);
  return result;
}

/**
 * Given an iterable and a function, call the function on each element of the iterable and return
 * an object with a property for each unique value yielded by the function, whose values are
 * arrays of the elements from which that property was obtained.
 * The order of elements in the returned arrays is consistent with the order they were yielded by
 * the iterable.
 * @template T
 * @param {Iterable<T>} iterable
 * @param {GroupingFunction} groupFn
 * @return {Record<string | number | symbol, T>}
 * @example group([1, 2, 3, 4], (x) => x % 2 === 0) --> {false: [1, 3], true: [2, 4]}
 */
export function group(iterable, groupFn) {
  /** @type {Record<string | number | symbol, T>} */
  const result = {};
  for (const item of iterable) {
    const groupValue = groupFn(item);
    if (groupValue in result) {
      result[groupValue].push(item);
    } else {
      result[groupValue] = [item];
    }
  }
  return result;
}

/**
 * Given an object, returns an array of `[key, value]` pairs, similiar to `Object.entries`, but with
 * the object's own Symbol keys included.
 * @param {object} object
 * @return {[string, any][]} An array of `[key, value]` pairs for each `string` and own `Symbol` key in `object`.
 */
export function entriesWithSymbols(object) {
  return Object.keys(object).concat(Object.getOwnPropertySymbols(object)).map((key) =>
    [key, object[key]],
  );
}
